import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, 
  Typography, 
  Modal,
  ModalDialog,
  ModalClose,
  IconButton,
  Card,
  Select,
  Option,
  Grid,
  Avatar,
  Chip,
  Divider
} from '@mui/joy';
import { supabase } from '../../supabaseClient';
import EnhancedButton from '../../components/EnhancedButton';
import EnhancedInput from '../../components/EnhancedInput';
import MetricCard from '../../components/MetricCard';
import LoadingSkeleton, { CardSkeleton } from '../../components/LoadingSkeleton';

const GISProjectsAdmin = () => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    external_map_url: '',
    client_id: '',
    project_id: ''
  });
  const [gisProjects, setGisProjects] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState(null);

  useEffect(() => {
    const fetchClients = async () => {
      const { data, error } = await supabase.from('clients').select('id, name').order('name');
      if (!error) setClients(data);
    };
    fetchClients();
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!form.client_id) {
        setProjects([]);
        return;
      }
      const { data, error } = await supabase
        .from('projects')
        .select('id, project_number')
        .eq('client_id', form.client_id)
        .order('project_number');
      if (!error) setProjects(data);
    };
    fetchProjects();
  }, [form.client_id]);

  useEffect(() => {
    fetchProjectsList();
  }, []);

  const fetchProjectsList = async () => {
    setInitialLoading(true);
    const { data, error } = await supabase
      .from('gis_projects')
      .select('id, name, description, external_map_url, created_at, project_id, client_id, projects(name), clients(name)')
      .order('created_at', { ascending: false });
    if (!error) setGisProjects(data);
    setInitialLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const openEditDialog = (project) => {
    setForm({
      name: project.name || '',
      description: project.description || '',
      external_map_url: project.external_map_url || '',
      client_id: project.client_id || '',
      project_id: project.project_id || ''
    });
    setEditingProjectId(project.id);
    setDialogOpen(true);
  };

  const handleDeleteProject = async (id) => {
    const { error } = await supabase.from('gis_projects').delete().eq('id', id);
    if (error) {
      console.error('Delete error:', error);
    } else {
      fetchProjectsList();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let error;
    const payload = {
      name: form.name,
      description: form.description,
      external_map_url: form.external_map_url,
      client_id: form.client_id || null,
      project_id: form.project_id || null
    };
    if (editingProjectId) {
      ({ error } = await supabase.from('gis_projects').update(payload).eq('id', editingProjectId));
    } else {
      ({ error } = await supabase.from('gis_projects').insert([payload]));
    }

    if (error) console.error('Submit error', error);
    else {
      alert(editingProjectId ? 'GIS Project updated successfully!' : 'GIS Project created successfully!');
      fetchProjectsList();
    }
    setLoading(false);
    setDialogOpen(false);
    setEditingProjectId(null);
  };

  // Helper functions
  const getProjectInitials = (projectName) => {
    return projectName?.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2) || 'GS';
  };

  const getProjectColor = (index) => {
    const colors = ['#00A884', '#17a2b8', '#28a745', '#6c757d', '#ffc107', '#dc3545'];
    return colors[index % colors.length];
  };

  // Statistics
  const totalGISProjects = gisProjects.length;
  const activeProjects = gisProjects.filter(p => p.external_map_url).length;
  const recentProjects = gisProjects.filter(p => {
    const created = new Date(p.created_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return created > thirtyDaysAgo;
  }).length;
  const uniqueClients = new Set(gisProjects.map(p => p.client_id)).size;

  if (initialLoading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Analytics Loading */}
        <Box sx={{ mb: 4 }}>
          <LoadingSkeleton height="32px" width="200px" sx={{ mb: 3 }} />
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
            <Grid xs={12} sm={6} md={3}><CardSkeleton /></Grid>
          </Grid>
        </Box>
        
        {/* Projects Loading */}
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={3}>
            {[...Array(6)].map((_, i) => (
              <Grid key={i} xs={12} sm={6} md={4}><CardSkeleton /></Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* GIS Analytics */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
          className="fade-in"
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            map
          </span>
          GIS Project Analytics
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }} className="fade-in-up">
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Total GIS Projects"
              value={totalGISProjects}
              icon="map"
              gradientColors={['#00A884', '#007E33']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Active Maps"
              value={activeProjects}
              icon="explore"
              gradientColors={['#17a2b8', '#138496']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Recent Projects"
              value={recentProjects}
              icon="schedule"
              gradientColors={['#28a745', '#218838']}
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <MetricCard
              title="Clients Served"
              value={uniqueClients}
              icon="business"
              gradientColors={['#6c757d', '#5a6268']}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Add GIS Project Section */}
      <Card 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              level="h4" 
              sx={{ 
                color: 'success.700',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mb: 1
              }}
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
                add_location
              </span>
              GIS Project Management
            </Typography>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Create and manage interactive mapping projects
            </Typography>
          </Box>
          
          <EnhancedButton
            gradient
            onClick={() => {
              setForm({
                name: '',
                description: '',
                external_map_url: '',
                client_id: '',
                project_id: ''
              });
              setEditingProjectId(null);
              setDialogOpen(true);
            }}
            startDecorator={
              <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                add
              </span>
            }
          >
            New GIS Project
          </EnhancedButton>
        </Box>
      </Card>

      {/* GIS Projects Grid */}
      <Box className="fade-in-up">
        <Typography 
          level="h4" 
          sx={{ 
            mb: 3, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
            satellite
          </span>
          GIS Project Gallery ({totalGISProjects})
        </Typography>
        
        <Grid container spacing={3}>
          {gisProjects.map((project, index) => (
            <Grid key={project.id} xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0, 168, 132, 0.15)',
                  }
                }}
                className="hover-lift"
              >
                {/* Project Header */}
                <Box 
                  sx={{ 
                    p: 3, 
                    background: `linear-gradient(135deg, ${getProjectColor(index)}15 0%, ${getProjectColor(index)}05 100%)`,
                    borderBottom: '1px solid',
                    borderColor: 'neutral.200'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: getProjectColor(index),
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getProjectInitials(project.projects?.name)}
                    </Avatar>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        level="title-sm" 
                        sx={{ 
                          color: 'success.700',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {project.projects?.name || 'Unnamed Project'}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {project.clients?.name || 'No Client'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Chip 
                    size="sm" 
                    variant="soft" 
                    sx={{
                      bgcolor: `${getProjectColor(index)}20`,
                      color: getProjectColor(index)
                    }}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '14px', fontFamily: 'Material Symbols Outlined' }}>
                        satellite
                      </span>
                    }
                  >
                    GIS Project
                  </Chip>
                </Box>

                {/* Project Details */}
                <Box sx={{ p: 3 }}>
                  <Typography 
                    level="body-sm" 
                    sx={{ 
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {project.description || 'No description provided'}
                  </Typography>

                  <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 3 }}>
                    📅 Created: {new Date(project.created_at).toLocaleDateString()}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {project.external_map_url && (
                      <EnhancedButton
                        size="sm"
                        variant="outlined"
                        component="a"
                        href={project.external_map_url}
                        target="_blank"
                        startDecorator={
                          <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                            open_in_new
                          </span>
                        }
                        sx={{ flex: 1 }}
                      >
                        View Map
                      </EnhancedButton>
                    )}
                    
                    <IconButton 
                      size="sm" 
                      variant="outlined" 
                      onClick={() => openEditDialog(project)}
                      sx={{ '&:hover': { borderColor: 'success.500', color: 'success.600' }}}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                        edit
                      </span>
                    </IconButton>
                    
                    <IconButton 
                      size="sm" 
                      color="danger" 
                      variant="outlined" 
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', fontFamily: 'Material Symbols Outlined' }}>
                        delete
                      </span>
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Enhanced GIS Project Modal */}
      <Modal open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <ModalDialog 
          sx={{ 
            maxWidth: '600px',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              {editingProjectId ? 'edit' : 'add_location'}
            </span>
            {editingProjectId ? 'Edit GIS Project' : 'Add New GIS Project'}
          </Typography>
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Client</Typography>
                  <Select
                    name="client_id"
                    value={form.client_id}
                    onChange={(e, val) => setForm(prev => ({ ...prev, client_id: val }))}
                    placeholder="Select client"
                    required
                    sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                  >
                    {clients.map((c) => (
                      <Option key={c.id} value={c.id}>{c.name}</Option>
                    ))}
                  </Select>
                </Box>
              </Grid>
              
              <Grid xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography level="body-sm" sx={{ mb: 1, fontWeight: 500 }}>Project</Typography>
                  <Select
                    name="project_id"
                    value={form.project_id}
                    onChange={(e, val) => setForm(prev => ({ ...prev, project_id: val }))}
                    placeholder="Select project"
                    required
                    disabled={!form.client_id}
                    sx={{ '&:focus-within': { borderColor: 'success.500', boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)' }}}
                  >
                    {projects.map((p) => (
                      <Option key={p.id} value={p.id}>{p.project_number}</Option>
                    ))}
                  </Select>
                </Box>
              </Grid>
              
              <Grid xs={12}>
                <EnhancedInput
                  label="Description"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Brief description of the GIS project..."
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      description
                    </span>
                  }
                />
              </Grid>
              
              <Grid xs={12}>
                <EnhancedInput
                  label="Map URL"
                  name="external_map_url"
                  value={form.external_map_url}
                  onChange={handleChange}
                  placeholder="https://example.com/map"
                  required
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                      link
                    </span>
                  }
                />
              </Grid>
              
              <Grid xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                  <EnhancedButton
                    variant="outlined"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </EnhancedButton>
                  <EnhancedButton
                    gradient
                    type="submit"
                    loading={loading}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                        {editingProjectId ? 'save' : 'add'}
                      </span>
                    }
                  >
                    {editingProjectId ? 'Update Project' : 'Save Project'}
                  </EnhancedButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default GISProjectsAdmin;
